import { useCallback, useEffect, useRef } from 'react';
import { FlowTypes } from 'store/constants';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { activeTabSelector } from 'store/slices/tabs';
import type { CurrencyBase, CurrencyType } from 'store/slices/wallet';
import {
  defaultFiatCurrencySelector,
  prevSelectedCurrencySelector,
  prevSelectedNetworkByCurrencySelector,
  setCurrentGroup,
  setSelectedCurrency,
} from 'store/slices/wallet';

import { useAppCore } from '../../providers/AppCoreProvider/AppCoreContext';
import { CURRENCY_CHANGE_REASON } from '../../providers/AppCoreProvider/AppCoreProvider';
import { useNavigateWithSearch } from '../hooks';
import { Routes } from '../routes';
import { PaymentType } from './types';

const flowToRouteMap = {
  [FlowTypes.deposit]: Routes.deposit,
  [FlowTypes.withdrawal]: Routes.withdrawal,
};

const getTabParams = (tab: string) => {
  switch (tab) {
    case PaymentType.FIAT:
      return {
        reason: CURRENCY_CHANGE_REASON.FIAT_TAB_SWITCH,
        isCrypto: false,
        isOnRamp: false,
      };
    case PaymentType.ON_RAMP:
      return {
        reason: CURRENCY_CHANGE_REASON.ONRAMP_TAB_SWITCH,
        isCrypto: false,
        isOnRamp: true,
      };
    case PaymentType.CRYPTO:
      return {
        reason: CURRENCY_CHANGE_REASON.CRYPTO_TAB_SWITCH,
        isCrypto: true,
        isOnRamp: false,
      };
    default:
      return null;
  }
};

export const useCurrencyFlow = (
  fiatCurrencies: CurrencyBase[],
  cryptoCurrencies: CurrencyBase[],
  onRampCurrencies: CurrencyBase[],
) => {
  const { handleCurrencyChange, resetFlowState } = useAppCore();
  const navigate = useNavigateWithSearch();
  const dispatch = useAppDispatch();
  const activeTab = useAppSelector(activeTabSelector);
  const currencyFlow = useAppSelector((state) => state.wallet.currencyFlow);
  const prevSelectedCurrency = useAppSelector(prevSelectedCurrencySelector);
  const defaultFiatCurrency = useAppSelector(defaultFiatCurrencySelector);
  const prevSelectedNetworkByCurrency = useAppSelector(prevSelectedNetworkByCurrencySelector);
  const currentActiveTab = useRef<string | null>(null);

  const findTargetWallet = useCallback(
    (tab: string): CurrencyBase | undefined => {
      const currentList =
        {
          [PaymentType.FIAT]: fiatCurrencies,
          [PaymentType.ON_RAMP]: onRampCurrencies,
          [PaymentType.CRYPTO]: cryptoCurrencies,
        }[tab] || [];

      if (currentList.length === 0) return undefined;

      const currencyMap = new Map(currentList.map((c) => [c.alpha3, c]));

      const codes: string[] = [
        /** * Priority 1: User's previous selection for the specific tab */
        tab === PaymentType.FIAT ? prevSelectedCurrency.fiat : null,
        tab === PaymentType.ON_RAMP ? prevSelectedCurrency.onRamp : null,
        tab === PaymentType.CRYPTO ? prevSelectedCurrency.crypto : null,
        /** * Priority 2: System default currency (only for FIAT tab) */
        tab === PaymentType.FIAT ? defaultFiatCurrency : null,
        /** * Priority 3: Hardcoded fallback to EUR (only for FIAT tab) */
        tab === PaymentType.FIAT ? 'EUR' : null,
      ].filter((code): code is string => typeof code === 'string');

      const targetCode = codes.find((code) => currencyMap.has(code));

      return (targetCode ? currencyMap.get(targetCode) : undefined) || currentList[0];
    },
    [fiatCurrencies, cryptoCurrencies, onRampCurrencies, defaultFiatCurrency, prevSelectedCurrency],
  );

  useEffect(() => {
    if (currentActiveTab.current === null || activeTab === null) {
      currentActiveTab.current = activeTab;
      return;
    }

    if (currentActiveTab.current === activeTab) return;

    resetFlowState();
    currentActiveTab.current = activeTab;
    dispatch(setCurrentGroup(null));

    const currentPath = window.location.pathname;
    const flow =
      currentPath.includes('withdrawal') && !currentPath.includes('reject-withdrawal')
        ? FlowTypes.withdrawal
        : FlowTypes.deposit;

    navigate(`/${flowToRouteMap[flow]}`);

    const params = getTabParams(activeTab);
    const targetWallet = findTargetWallet(activeTab);

    if (params && targetWallet) {
      const { reason, isCrypto, isOnRamp } = params;
      const savedNetwork = isCrypto ? prevSelectedNetworkByCurrency[targetWallet.alpha3] : undefined;

      dispatch(setSelectedCurrency(targetWallet));
      if (savedNetwork) dispatch(setCurrentGroup(savedNetwork));

      handleCurrencyChange({
        currency: targetWallet,
        isCrypto,
        isOnRamp,
        tab: activeTab as CurrencyType,
        reason,
        currentGroup: savedNetwork || undefined,
      });
    }
  }, [activeTab, findTargetWallet, handleCurrencyChange, dispatch, navigate, resetFlowState]);

  return {
    activeTab,
    currencyFlow,
  };
};
